/**
 * sidebar-sections.ts — pure data module for Sidebar.tsx navigation structure.
 *
 * Extracted to a separate file so that the react-refresh/only-export-components
 * lint rule is satisfied: Sidebar.tsx exports only the Sidebar component, and
 * this file exports only data/types (no React components).
 *
 * IMPORTANT — SINGLE SOURCE OF TRUTH:
 * The `scope` and `permissions` fields on every SECTIONS entry MUST match the
 * corresponding ModuleNavItem in navigation.ts. The parity test in
 * Sidebar.test.tsx catches any drift.
 *
 * W3 fix (ADR-0024): SECTIONS is now importable by Sidebar.test.tsx for the
 * parity assertion that SECTIONS scope === navigationModules scope for every
 * matched path.
 */
import type React from 'react'
import {
  Inbox,
  AlertTriangle,
  Briefcase,
  Users,
  Lock,
  FileText,
  BarChart3,
  Brain,
  Workflow,
  ClipboardCheck,
  ClipboardList,
  Settings,
  Receipt,
  LayoutDashboard,
  Shield,
  ScrollText,
  UserCog,
  UserCircle,
  Activity,
  LayoutGrid,
  ShieldCheck,
  Send,
  Stethoscope,
  Sparkles,
  GitBranch,
  Building2,
  Megaphone,
  Database,
  GanttChart,
  TrendingUp,
  KeyRound,
  Fingerprint,
  Webhook,
  Server,
  FileCode2,
  History,
  BookOpen,
  FlaskConical,
  Cpu,
  Newspaper,
  Rss,
  ListChecks,
  UserSearch,
  Settings2,
  Plug,
  ToggleLeft,
  HeartPulse,
  Monitor,
  FileCheck2,
  Landmark,
} from 'lucide-react'
import type { NavScope } from '@/lib/navigation'

export interface NavItem {
  kind?: 'item'
  to: string
  label: string
  Icon: React.ComponentType<{ size?: number; className?: string }>
  /**
   * Permission(s) required to see this link. Semantics match
   * canSeeNavItem: the link shows if the principal holds **any** of
   * the listed permissions (within the scope gate). Omit or pass
   * empty array for links everyone with a session can see (within scope).
   */
  permissions?: readonly string[]
  /**
   * Scope gate. Controls which class of user can see this item.
   *   'platform' — requires Me.is_platform_admin (no system:admin bypass).
   *   'tenant'   — standard permission gate (system:admin wildcard applies).
   *   'both'     — visible to both scopes (default when omitted).
   *
   * MUST stay in sync with the corresponding ModuleNavItem.scope in navigation.ts.
   * The parity test in Sidebar.test.tsx catches divergence.
   */
  scope?: NavScope
  /**
   * @deprecated Use `scope: 'platform'` instead.
   * Legacy alias for `scope: 'platform'`. The itemVisible predicate
   * treats this identically to `scope: 'platform'`.
   */
  platformAdminOnly?: boolean
}

/**
 * Sub-group divider inside a section. Renders as a small uppercase
 * label with a thin top spacer so a long section reads as a chunked
 * list rather than one wall of links. The visibility filter hides
 * orphan subheaders (no visible items below until the next subheader
 * or end-of-section) so the menu never shows an empty label.
 */
export interface NavSubheader {
  kind: 'subheader'
  label: string
}

export type NavEntry = NavItem | NavSubheader

export interface NavSection {
  label: string | null // null = no header (used by Settings)
  items: readonly NavEntry[]
}

/**
 * Sidebar information architecture (enterprise-app conventions).
 *
 * Two clusters separated visually by section borders:
 *   • Workstream surfaces (top) — daily-use entries ordered by an
 *     analyst's typical session: land → triage → comply → analyse → ML.
 *   • Administration surfaces (bottom) — config, telemetry, audit;
 *     ordered org-people → platform-knobs → data-plane → audit-health.
 *
 * Sections registered in `navigationModules` (navigation.ts) are
 * rendered as a single consolidated NavLink + horizontal top submenu.
 * Sections not in `navigationModules` (Platform, Settings) stay flat.
 *
 *   1. Home               (consolidated)  personal landing
 *   2. Dashboards         (consolidated)
 *   3. Investigations     (consolidated)  analyst hot-path
 *   4. Compliance         (consolidated)  regulator workflows
 *   5. Sanctions & Screening (consolidated)
 *   6. Adverse Media      (consolidated)
 *   7. Detection          (consolidated)  rules, models, governance
 *   8. Administration     (consolidated)  access, platform config
 *   9. Ingestion          (consolidated)  databus + simulator
 *  10. Platform           (FLAT)          multi-tenant admin
 *  11. Operations         (consolidated)  live-health, audit, dev-only
 *
 *   Settings sits ungrouped beneath all sections (FLAT).
 */
export const SECTIONS: readonly NavSection[] = [
  {
    label: 'Home',
    items: [
      { to: '/my-dashboard', label: 'My Dashboard', Icon: UserCircle, scope: 'both' },
      { to: '/inbox', label: 'Inbox', Icon: Inbox, permissions: ['inbox:read'], scope: 'tenant' },
    ] as readonly NavEntry[],
  },
  {
    label: 'Dashboards',
    items: [
      { to: '/dashboard', label: 'Executive Dashboard', Icon: LayoutDashboard, permissions: ['system:admin'], scope: 'platform' },
      { to: '/analytics', label: 'Trend Analytics', Icon: BarChart3, permissions: ['audit:read'], scope: 'tenant' },
      { to: '/operations', label: 'Operations Health', Icon: Activity, permissions: ['audit:read'], scope: 'tenant' },
      { to: '/highlights', label: 'Risk Heatmap', Icon: TrendingUp, permissions: ['audit_log:read'], scope: 'tenant' },
      { to: '/dashboard/hq', label: 'HQ Rollup', Icon: Building2, permissions: ['audit:read'], scope: 'tenant' },
      { to: '/role-dashboards', label: 'Role Workspaces', Icon: UserCog, permissions: ['audit:read'], scope: 'tenant' },
      { to: '/portfolio', label: 'All Dashboards', Icon: LayoutGrid, permissions: ['audit:read'], scope: 'tenant' },
    ],
  },
  {
    // Analyst's daily lane. Ordered alerts → cases → customers →
    // transactions → network → consortium.
    label: 'Investigations',
    items: [
      { to: '/alerts', label: 'Alerts', Icon: AlertTriangle, permissions: ['cases:read'], scope: 'tenant' },
      { to: '/cases', label: 'Cases', Icon: Briefcase, permissions: ['cases:read'], scope: 'tenant' },
      // Fraud-queues consolidation: moved from Operations (queue triage is
      // analyst work). State Machine stays in Operations (live-state
      // introspection, not triage).
      { to: '/workflow', label: 'Workflow Queues', Icon: Workflow, permissions: ['workflow:read'], scope: 'tenant' },
      // Backend canonical view permission: audit:read (challenges_admin_handler.go).
      // analyst, compliance_officer, and viewer all hold audit:read.
      // /admin/challenges is the operational challenge feed, not admin-config.
      { to: '/admin/challenges', label: 'Customer Challenges', Icon: Fingerprint, permissions: ['audit:read'], scope: 'tenant' },
      { to: '/customers', label: 'Customers', Icon: Users, permissions: ['customers:read'], scope: 'tenant' },
      { to: '/transactions', label: 'Transactions', Icon: Receipt, permissions: ['transactions:read'], scope: 'tenant' },
      // AF-404 — Network Explorer.
      { to: '/investigations/network', label: 'Network Explorer', Icon: GitBranch, permissions: ['cases:read', 'customers:read'], scope: 'tenant' },
      // AF-406 — Consortium Intelligence feed.
      { to: '/intelligence/consortium', label: 'Consortium Intel', Icon: Megaphone, permissions: ['consortium:read'], scope: 'tenant' },
    ],
  },
  {
    // Regulatory + compliance. Dashboard → SAR/STR filings →
    // enforcement (Blocks) → output (Reports) → people (Handovers).
    label: 'Compliance',
    items: [
      { to: '/compliance', label: 'Compliance Dashboard', Icon: ShieldCheck, permissions: ['audit:read'], scope: 'tenant' },
      { to: '/sars', label: 'SAR / STR', Icon: FileText, permissions: ['sars:read'], scope: 'tenant' },
      { to: '/blocks', label: 'Blocks', Icon: Lock, permissions: ['blocks:read'], scope: 'tenant' },
      { to: '/reports', label: 'Reports', Icon: FileText, permissions: ['reports:read'], scope: 'tenant' },
      { to: '/handovers', label: 'Handovers', Icon: ClipboardCheck, permissions: ['handovers:read'], scope: 'tenant' },
    ],
  },
  {
    label: 'Sanctions & Screening',
    items: [
      { to: '/screening/dashboard', label: 'Dashboard', Icon: ShieldCheck, permissions: ['screening:read'], scope: 'tenant' },
      {
        to: '/screening/console',
        label: 'Console',
        Icon: Activity,
        // ADR-0023: Screening Console is a platform-level engine-management surface.
        // Tenant users keep Dashboard + History; Console is platform-admin only.
        scope: 'platform',
      },
      { to: '/screening/history', label: 'History', Icon: History, permissions: ['screening:read'], scope: 'tenant' },
    ],
  },
  {
    // ADVERSE MEDIA SCOPE (Phase 4 — ADR-0024, see navigation.ts for full rationale):
    //   Tenant-data surfaces (Dashboard, Feeds, Classifications, Profiles, Eval set):
    //     scope:'tenant' — data is tenant-private (migration 0292); platform admins
    //     see these only in tenant-view mode.
    //   Prompts: scope:'platform' — shared LLM prompt templates managed by platform
    //     admins; tenant users do not configure prompts.
    label: 'Adverse Media',
    items: [
      { to: '/adverse-media/dashboard',       label: 'Dashboard',       Icon: Newspaper,   permissions: ['adverse_media:read'], scope: 'tenant' as NavScope },
      { to: '/adverse-media/feeds',           label: 'Feeds',           Icon: Rss,         permissions: ['adverse_media:read'], scope: 'tenant' as NavScope },
      { to: '/adverse-media/classifications', label: 'Classifications', Icon: ListChecks,  permissions: ['adverse_media:read'], scope: 'tenant' as NavScope },
      { to: '/adverse-media/profiles',        label: 'Profiles',        Icon: UserSearch,  permissions: ['adverse_media:read'], scope: 'tenant' as NavScope },
      { to: '/adverse-media/eval-set',        label: 'Eval set',        Icon: FlaskConical, permissions: ['adverse_media:read'], scope: 'tenant' as NavScope },
      { to: '/adverse-media/prompts',         label: 'Prompts',         Icon: Brain,       permissions: ['adverse_media:prompts_admin'], scope: 'platform' as NavScope },
    ],
  },
  {
    label: 'Detection',
    items: [
      { kind: 'subheader', label: 'Rules & scenarios' },
      // Platform-admin cross-tenant scenario/rule management (scope:'platform').
      { to: '/admin/scenarios', label: 'Scenarios', Icon: GitBranch, permissions: ['system:admin'], scope: 'platform' },
      { to: '/admin/rules', label: 'Rules', Icon: GanttChart, permissions: ['system:admin'], scope: 'platform' },
      // Tenant-admin self-service scenario/rule management (scope:'tenant').
      // Visible to users with scenarios:read — tenant-admins manage their own copy.
      // Mutually exclusive with the platform entries above via scope gating.
      { to: '/tenant-admin/scenarios', label: 'My Scenarios', Icon: GitBranch, permissions: ['scenarios:read'], scope: 'tenant' },
      { to: '/tenant-admin/rules', label: 'My Rules', Icon: GanttChart, permissions: ['scenarios:read'], scope: 'tenant' },
      { kind: 'subheader', label: 'Machine learning' },
      { to: '/ml', label: 'Model Registry', Icon: Brain, permissions: ['ml:read'], scope: 'tenant' },
      { to: '/ml/monitoring', label: 'Model Monitoring', Icon: Stethoscope, permissions: ['ml:read'], scope: 'tenant' },
      { to: '/ml/governance', label: 'Explainable AI', Icon: Sparkles, permissions: ['ml:read'], scope: 'tenant' },
      { to: '/admin/ml', label: 'Closed-Loop Feedback', Icon: Brain, permissions: ['ml_feedback:read'], scope: 'tenant' },
      { kind: 'subheader', label: 'Governance' },
      { to: '/admin/maker-checker', label: 'Maker-Checker', Icon: ShieldCheck, permissions: ['system:admin'], scope: 'platform' },
      { to: '/admin/fraud-to-aml', label: 'Fraud → AML', Icon: Send, permissions: ['system:admin'], scope: 'platform' },
      { to: '/admin/communications', label: 'Communications', Icon: Megaphone, permissions: ['system:admin'], scope: 'platform' },
    ] as readonly NavEntry[],
  },
  {
    label: 'Administration',
    items: [
      { kind: 'subheader', label: 'Access' },
      {
        to: '/admin/users',
        label: 'Users & RBAC',
        Icon: UserCog,
        permissions: ['system:admin'],
        // ADR-0023: platform-admin-only. Tenant-admins hold system:admin for their
        // tenant but /admin/users now requires actor_scope_type=platform on the backend.
        scope: 'platform',
      },
      {
        to: '/admin/roles',
        label: 'Role Tiers',
        Icon: Shield,
        permissions: ['system:admin'],
        // ADR-0023: global RBAC catalogue is platform-admin-only.
        scope: 'platform',
      },
      // ADR-0026 Phase B: /admin/branches and /admin/teams are NOW platform-only.
      // Tenant admins self-manage via /tenant-admin/branches and /tenant-admin/teams.
      { to: '/admin/branches', label: 'Branches', Icon: Building2, permissions: ['branches:read'], scope: 'platform' },
      { to: '/admin/teams', label: 'Teams', Icon: Users, permissions: ['teams:read'], scope: 'platform' },
      // access:grant and system:admin are platform-scoped.
      { to: '/access-requests', label: 'Access Requests', Icon: KeyRound, permissions: ['access:grant', 'system:admin'], scope: 'platform' },
      { kind: 'subheader', label: 'Platform config' },
      { to: '/admin/jobs', label: 'Scheduled Jobs', Icon: Workflow, permissions: ['system:admin'], scope: 'platform' },
      { to: '/admin/challenges/settings', label: 'Challenge Plane Settings', Icon: Fingerprint, permissions: ['system:admin'], scope: 'platform' },
      { to: '/admin/api-keys', label: 'API Keys', Icon: KeyRound, permissions: ['system:admin'], scope: 'platform' },
      { to: '/admin/webhooks', label: 'Webhooks', Icon: Webhook, permissions: ['system:admin'], scope: 'platform' },
      { to: '/admin/observability/access-control', label: 'Observability ACL', Icon: ShieldCheck, permissions: ['observability:read', 'system:admin'], scope: 'platform' },
      {
        to: '/admin/integrations',
        label: 'Integrations',
        Icon: Plug,
        // ADR-0023: platform-level vendor registry (Dojah, OFAC, NIBSS, etc.).
        // Tenant users must not configure it.
        scope: 'platform',
      },
      {
        to: '/admin/platform/feature-flags',
        label: 'Platform Feature Flags',
        Icon: ToggleLeft,
        // ADR-0023: platform-level feature gates.
        scope: 'platform',
      },
    ] as readonly NavEntry[],
  },
  {
    label: 'Ingestion',
    items: [
      { kind: 'subheader', label: 'Enterprise Databus' },
      // All databus:* permissions are platform-scoped.
      { to: '/admin/databus/tenants', label: 'Tenant Management', Icon: Building2, permissions: ['databus:admin'], scope: 'platform' },
      { to: '/admin/databus/ingestion-channels/new', label: 'Add Connector', Icon: Sparkles, permissions: ['databus:admin'], scope: 'platform' },
      { to: '/admin/databus', label: 'Ops Dashboard', Icon: BarChart3, permissions: ['databus:read'], scope: 'platform' },
      { to: '/admin/databus/adapters', label: 'Tenants & Adapters', Icon: Server, permissions: ['databus:read'], scope: 'platform' },
      { to: '/admin/databus/mappings', label: 'Mapping Editor', Icon: FileCode2, permissions: ['databus:read'], scope: 'platform' },
      { to: '/admin/databus/schemas', label: 'Schema Registry', Icon: BookOpen, permissions: ['databus:read'], scope: 'platform' },
      { to: '/admin/databus/dlq', label: 'Dead-Letter Queue', Icon: AlertTriangle, permissions: ['databus:read'], scope: 'platform' },
      { to: '/admin/databus/recon', label: 'Reconciliation', Icon: History, permissions: ['databus:read'], scope: 'platform' },
      { to: '/admin/databus/lineage', label: 'Lineage Explorer', Icon: GitBranch, permissions: ['databus:read'], scope: 'platform' },
      { to: '/admin/databus/settings', label: 'Databus Settings', Icon: Settings2, permissions: ['databus:read'], scope: 'platform' },
      { kind: 'subheader', label: 'Banking simulator' },
      { to: '/admin/databus-simulator', label: 'Runs', Icon: FlaskConical, permissions: ['databus:admin'], scope: 'platform' },
      { to: '/admin/databus-simulator/scenarios', label: 'Scenarios', Icon: GanttChart, permissions: ['databus:admin'], scope: 'platform' },
      { to: '/admin/databus-simulator/chaos', label: 'Chaos drill', Icon: AlertTriangle, permissions: ['databus:admin'], scope: 'platform' },
      { to: '/admin/databus-simulator/allowlist', label: 'Allowlist', Icon: KeyRound, permissions: ['databus:admin'], scope: 'platform' },
      { to: '/admin/databus-simulator/system', label: 'Sim services', Icon: Cpu, permissions: ['databus:admin'], scope: 'platform' },
    ] as readonly NavEntry[],
  },
  {
    // ADR-0023 — Tenant self-service administration.
    // ADR-0025 — Tenant-scoped role management added (Roles item).
    // ADR-0026 Phase C — Promoted to a navigationModule ('tenant-admin') so the
    // sidebar renders a single consolidated "Administration" link and the 5 items
    // appear as a horizontal ModuleTopNav tab strip (matching the platform module
    // pattern). The SECTIONS label is 'Tenant Admin' (distinct from the platform
    // module section label 'Administration') so that moduleByLabel in Sidebar.tsx
    // resolves each section to its own module without collision.
    //
    // All items are scope:'tenant' — visible to tenant-scoped users and platform
    // admins in tenant-view mode only. Platform admins in platform mode never see
    // this module (canSeeNavItem returns false for scope:'tenant' in platform mode).
    label: 'Tenant Admin',
    items: [
      {
        to: '/tenant-admin/users',
        label: 'Users & Roles',
        Icon: UserCog,
        permissions: ['tenant_users:admin'],
        scope: 'tenant',
      },
      {
        // ADR-0025: Tenant-scoped role management.
        // Gated on tenant_roles:read (scope:'tenant') — matches the backend permission exactly.
        // Platform /admin/roles is NOT exposed here; tenant actors must not access it.
        to: '/tenant-admin/roles',
        label: 'Roles',
        Icon: Shield,
        permissions: ['tenant_roles:read'],
        scope: 'tenant',
      },
      {
        // ADR-0025 Follow-on: Tenant-internal maker-checker approvals inbox.
        // ADR-0026 Phase B: inbox now covers role, branch, and team change approvals.
        // Slice 3: adds SSO config approvals. Slice 5: adds API key revocation approvals.
        // Slice 6: adds webhook creation approvals (tenant_webhooks:approve).
        // Slice 8: adds banking settings update approvals (tenant_bank_settings:approve).
        // Slice 9: adds challenge settings update approvals (tenant_challenge_settings:approve).
        // Gated on ANY of the eight approve perms (scope:'tenant') — a principal
        // holding any approve perm is a valid checker and should see the inbox.
        // canSeeNavItem uses permissions.some() so the array is treated as OR.
        to: '/tenant-admin/approvals',
        label: 'Approvals',
        Icon: ClipboardList,
        permissions: ['tenant_roles:approve', 'tenant_branches:approve', 'tenant_teams:approve', 'tenant_sso:approve', 'tenant_api_keys:approve', 'tenant_webhooks:approve', 'tenant_bank_settings:approve', 'tenant_challenge_settings:approve'],
        scope: 'tenant',
      },
      {
        // ADR-0026 Phase B: Tenant self-service branch management.
        // Gated on tenant_branches:read (scope:'tenant').
        // Distinct from the platform /admin/branches page (platform-only).
        to: '/tenant-admin/branches',
        label: 'Branches',
        Icon: Building2,
        permissions: ['tenant_branches:read'],
        scope: 'tenant',
      },
      {
        // ADR-0026 Phase B: Tenant self-service team management.
        // Gated on tenant_teams:read (scope:'tenant').
        // Distinct from the platform /admin/teams page (platform-only).
        to: '/tenant-admin/teams',
        label: 'Teams',
        Icon: Users,
        permissions: ['tenant_teams:read'],
        scope: 'tenant',
      },
      {
        // Tenant session management — list and revoke sessions for tenant members.
        // Gated on tenant_users:admin (scope:'tenant').
        // Uses /tenants/self/sessions — tenant is derived server-side from JWT.
        // DO NOT link to the platform /admin/sessions surface from here.
        to: '/tenant-admin/sessions',
        label: 'Sessions',
        Icon: Monitor,
        permissions: ['tenant_users:admin'],
        scope: 'tenant',
      },
      {
        // Tenant self-service SSO configuration.
        // Gated on tenant_sso:read (scope:'tenant') — mirrors PermTenantSSORead.
        // Write affordances additionally require tenant_sso:admin.
        // Uses /tenants/self/sso-config — tenant is derived server-side from JWT.
        // Mutations are maker-checker gated by default (security-sensitive).
        to: '/tenant-admin/sso',
        label: 'SSO',
        Icon: ShieldCheck,
        permissions: ['tenant_sso:read'],
        scope: 'tenant',
      },
      {
        // Tenant self-service JIT access approval queue.
        // Gated on tenant_jit:approve (scope:'tenant').
        // tenant_jit:read is NeverGrantableToTenantRole — read access is implied by
        // the approve permission (future read-only delegation pattern, not yet active).
        // Uses /tenants/self/jit-access/pending — tenant derived server-side from JWT.
        // Self-approval is blocked server-side (403 cannot_self_approve).
        to: '/tenant-admin/jit-access',
        label: 'JIT Access',
        Icon: KeyRound,
        permissions: ['tenant_jit:approve'],
        scope: 'tenant',
      },
      {
        // Slice 5: Tenant self-service API key management.
        // Gated on tenant_api_keys:read (scope:'tenant') — mirrors PermTenantAPIKeysRead.
        // Write affordances (create, revoke) additionally require tenant_api_keys:admin.
        // Revoke mutations are maker-checker gated by default (api_key.revoke approval flow).
        // Uses /tenants/self/api-keys — tenant derived server-side from JWT.
        // Distinct from platform /admin/api-keys (platform-only, not accessible here).
        to: '/tenant-admin/api-keys',
        label: 'API Keys',
        Icon: KeyRound,
        permissions: ['tenant_api_keys:read'],
        scope: 'tenant',
      },
      {
        // Slice 6: Tenant self-service webhook management.
        // Gated on tenant_webhooks:read (scope:'tenant') — mirrors PermTenantWebhooksRead.
        // Write affordances (create, enable, disable) additionally require tenant_webhooks:admin.
        // Creation is maker-checker gated (webhook.create; entity_type: tenant_webhook_creation).
        // Enable and disable are single-operator.
        // Uses /tenants/self/webhooks — tenant derived server-side from JWT.
        // Distinct from platform /admin/webhooks (platform-only, not accessible here).
        to: '/tenant-admin/webhooks',
        label: 'Webhooks',
        Icon: Webhook,
        permissions: ['tenant_webhooks:read'],
        scope: 'tenant',
      },
      {
        // Slice 7: Tenant self-service access-request approver inbox.
        // Gated on tenant_access:approve (scope:'tenant').
        // Note: tenant_access:read is NeverGrantableToTenantRole and is NOT the gate —
        // read access is implied by the approve permission (same design as JIT Access).
        // DISTINCT from JIT Access (/tenant-admin/jit-access):
        //   JIT Access = temporary role elevation.
        //   Access Requests = temporary access to a specific case or customer UUID.
        // Uses /tenants/self/access-requests — tenant derived server-side from JWT.
        // Distinct from platform /access-requests (uses access:grant, not tenant_access:approve).
        to: '/tenant-admin/access-requests',
        label: 'Access Requests',
        Icon: FileCheck2,
        permissions: ['tenant_access:approve'],
        scope: 'tenant',
      },
      {
        // Slice 8: Tenant self-service banking settings management.
        // Gated on tenant_bank_settings:read (scope:'tenant') — mirrors PermTenantBankSettingsRead.
        // Write affordances (propose) additionally require tenant_bank_settings:admin.
        // All proposals are maker-checker gated (banking_settings.update; entity_type:
        //   tenant_banking_settings_update). No direct-apply path exists.
        // Uses /tenants/self/bank-settings — tenant derived server-side from JWT.
        to: '/tenant-admin/bank-settings',
        label: 'Bank Settings',
        Icon: Landmark,
        permissions: ['tenant_bank_settings:read'],
        scope: 'tenant',
      },
      {
        // Slice 9: Tenant self-service challenge plane settings.
        // Gated on tenant_challenge_settings:read (scope:'tenant') — mirrors PermTenantChallengeSettingsRead.
        // Write affordances (propose) additionally require tenant_challenge_settings:admin.
        // All proposals are maker-checker gated (challenge_settings.update; entity_type:
        //   tenant_challenge_settings_update). No direct-apply path exists.
        // LIVE: liveness_min_score, liveness_face_match_min_score.
        // DEFERRED: vendor pins + deadline/attempts overrides (inline-liveness channel not yet shipped).
        // Uses /tenants/self/challenge-settings — tenant derived server-side from JWT.
        // Distinct from platform /admin/challenges/settings (system:admin, platform-only).
        to: '/tenant-admin/challenge-settings',
        label: 'Challenge Settings',
        Icon: Fingerprint,
        permissions: ['tenant_challenge_settings:read'],
        scope: 'tenant',
      },
    ] as readonly NavEntry[],
  },
  {
    // Platform Administration — multi-tenant management console.
    // NOT in navigationModules — stays flat (single item; no submenu needed).
    // Label is "Platform" (not "Tenant Management") to avoid name collision with
    // the Ingestion module's "Tenant Management" submenu item.
    label: 'Platform',
    items: [
      {
        to: '/platform',
        label: 'Platform',
        Icon: Building2,
        // ADR-0023: platform section is visible only to platform-scope admins.
        scope: 'platform',
      },
    ] as readonly NavEntry[],
  },
  {
    label: 'Operations',
    items: [
      { kind: 'subheader', label: 'Live health' },
      { to: '/admin/system-status', label: 'System Status', Icon: HeartPulse, permissions: ['system:read'], scope: 'platform' },
      { to: '/admin/services', label: 'Services Control', Icon: Cpu, permissions: ['system:admin'], scope: 'platform' },
      { to: '/admin/ingestion', label: 'Ingestion Engine', Icon: Database, permissions: ['system:read'], scope: 'platform' },
      { to: '/admin/batch', label: 'Batch Processing', Icon: GanttChart, permissions: ['system:read'], scope: 'platform' },
      { to: '/admin/outbox-replay', label: 'Outbox Replay', Icon: History, permissions: ['outbox:request', 'outbox:approve'], scope: 'platform' },
      // Workflow Queues moved to Investigations (Fraud-queues consolidation).
      { to: '/workflow/state-machine', label: 'State Machine', Icon: GitBranch, permissions: ['workflow:read'], scope: 'tenant' },
      { kind: 'subheader', label: 'Audit' },
      { to: '/admin/audit', label: 'Audit Log', Icon: ScrollText, permissions: ['audit_log:read'], scope: 'tenant' },
      { to: '/admin/access-log', label: 'Access Logs', Icon: KeyRound, permissions: ['access_log:read'], scope: 'platform' },
      { kind: 'subheader', label: 'Developer tools' },
      { to: '/admin/demo-data', label: 'Demo Data', Icon: Sparkles, permissions: ['demo:admin', 'system:admin'], scope: 'platform' },
    ] as readonly NavEntry[],
  },
  {
    label: null,
    // Settings: AMBIGUOUS (system:admin only) → defaulting to scope:'platform' (fail-closed).
    // Filed for product approval.
    items: [{ to: '/settings', label: 'Settings', Icon: Settings, permissions: ['system:admin'], scope: 'platform' as NavScope }],
  },
]
